/**
 * IssueAnalyticsPage — Admin dashboard with aggregate analytics for issue reports.
 *
 * Shows breakdowns by category, severity, status, a 30-day trend, and
 * summary stats (GitHub-linked, with screenshot). Uses MUI LinearProgress
 * for bar visualizations — consistent with the existing AnalyticsDashboard.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import issueReportService, {
  type IssueReportAnalytics,
} from '../../services/issueReportService';
import { useAppSelector } from '../../hooks/reduxHooks';
import PageHeaderNavigation from '../../components/common/PageHeaderNavigation';

const SEVERITY_COLORS: Record<string, 'error' | 'warning' | 'default' | 'success'> = {
  critical: 'error',
  high: 'warning',
  medium: 'default',
  low: 'success',
};

const STATUS_COLORS: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
  open: 'error',
  investigating: 'warning',
  resolved: 'success',
  wont_fix: 'default',
};

const StatBox: React.FC<{ label: string; value: number | string; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
    {icon && <Box sx={{ mb: 0.5 }}>{icon}</Box>}
    <Typography variant="h4" color="primary.main">{value}</Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Box>
);

const BreakdownCard: React.FC<{
  title: string;
  data: { label: string; count: number }[];
  total: number;
  colorMap?: Record<string, 'error' | 'warning' | 'default' | 'success'>;
}> = ({ title, data, total, colorMap }) => {
  const sorted = [...data].sort((a, b) => b.count - a.count);
  const max = sorted.length > 0 ? sorted[0].count : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'h6', fontWeight: 500 }} />
      <Divider />
      <CardContent>
        {sorted.length === 0 ? (
          <Typography color="text.secondary">No data</Typography>
        ) : (
          sorted.map((item) => (
            <Box key={item.label} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                {colorMap ? (
                  <Chip
                    label={item.label}
                    size="small"
                    color={colorMap[item.label] || 'default'}
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {item.label.replace(/_/g, ' ')}
                  </Typography>
                )}
                <Typography variant="body2" fontWeight={500}>{item.count}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={max ? (item.count / max) * 100 : 0}
                sx={{ height: 6, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.disabled">
                {total ? Math.round((item.count / total) * 100) : 0}% of total
              </Typography>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
};

const TrendCard: React.FC<{ data: { date: string; count: number }[] }> = ({ data }) => {
  const max = data.length > 0 ? Math.max(...data.map((d) => d.count)) : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="Reports Over Time (30 days)"
        titleTypographyProps={{ variant: 'h6', fontWeight: 500 }}
        avatar={<TrendingUpIcon color="primary" />}
      />
      <Divider />
      <CardContent>
        {data.length === 0 ? (
          <Typography color="text.secondary">No reports in the last 30 days</Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 120, overflowX: 'auto' }}>
            {data.map((day) => (
              <Box
                key={day.date}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 28,
                  height: '100%',
                  justifyContent: 'flex-end',
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.6rem', mb: 0.25 }}>
                  {day.count}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    minWidth: 16,
                    height: `${max ? (day.count / max) * 80 : 0}%`,
                    minHeight: day.count > 0 ? 4 : 0,
                    bgcolor: 'primary.main',
                    borderRadius: 0.5,
                    opacity: 0.7,
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.disabled', mt: 0.25 }}>
                  {day.date.slice(5)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const IssueAnalyticsPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';

  const [analytics, setAnalytics] = useState<IssueReportAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await issueReportService.getIssueReportAnalytics();
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchAnalytics();
  }, [isAdmin, fetchAnalytics]);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Admin access required to view this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeaderNavigation
        title="Issue Analytics"
        subtitle="Aggregate insights from in-app issue reports"
      />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button startIcon={<RefreshIcon />} onClick={fetchAnalytics} variant="outlined" size="small">
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !analytics ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <BugReportIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No analytics data available.</Typography>
          </Paper>
        ) : (
          <Stack spacing={3}>
            {/* Summary stats */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <StatBox label="Total Reports" value={analytics.total} icon={<BugReportIcon color="primary" />} />
              <StatBox label="GitHub-Linked" value={analytics.github_linked} />
              <StatBox label="With Screenshot" value={analytics.with_screenshot} />
              <StatBox
                label="Resolution Rate"
                value={
                  analytics.total > 0
                    ? `${Math.round(
                        ((analytics.by_status.find((s) => s.label === 'resolved')?.count || 0) /
                          analytics.total) *
                          100,
                      )}%`
                    : '—'
                }
              />
            </Box>

            {/* Breakdowns */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <BreakdownCard
                  title="By Category"
                  data={analytics.by_category}
                  total={analytics.total}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <BreakdownCard
                  title="By Severity"
                  data={analytics.by_severity}
                  total={analytics.total}
                  colorMap={SEVERITY_COLORS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <BreakdownCard
                  title="By Status"
                  data={analytics.by_status}
                  total={analytics.total}
                  colorMap={STATUS_COLORS}
                />
              </Grid>
            </Grid>

            {/* Trend */}
            <TrendCard data={analytics.daily_trend} />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default IssueAnalyticsPage;
